import {
  State,
  appendValue,
  duplicateValueWithIndex,
  mergeValue,
  moveTo,
  removeValueWithIndex,
  replaceValuesWithIndexes,
  setValue
} from '@btime/state-monad'
import { curry } from 'ramda'
import { generateContext } from '@btime/react-contexts'
import { uniqId } from '@btime/helpers'
import { useOnUnload } from '@btime/react-hooks'
import React, { useCallback, useContext, useReducer } from 'react'

import { Reducer } from '../../../../utils/reducer/chain'
import { handleState } from '../../../../utils/reducer/basicStateHandler'
import { useLinkTreeItems } from '../_load/LinkTreeItemsLoader'

const { StateContext, ActionsContext, Provider } = generateContext()
export const useLinkTreeForm = () => useContext(StateContext)
export const useLinkTreeFormActions = () => useContext(ActionsContext)

const CHANGE_LINKTREE_ITEM = 'CHANGE_LINKTREE_ITEM'
const ADD_NEW_LINKTREE_ITEM = 'ADD_NEW_LINKTREE_ITEM'
const DUPLICATE_LINKTREE_ITEM = 'DUPLICATE_LINKTREE_ITEM'
const REMOVE_LINKTREE_ITEM = 'REMOVE_LINKTREE_ITEM '
const MOVE_UP_LINKTREE_ITEM = 'MOVE_UP_LINKTREE_ITEM'
const MOVE_DOWN_LINKTREE_ITEM = 'MOVE_DOWN_LINKTREE_ITEM'

const reducer = (state, action) =>
  Reducer(state, action)
    .map(
      handleState(CHANGE_LINKTREE_ITEM, (state, action) => {
        const { index, field, value } = action.payload

        return State.of({ ...state, touched: true })
          .map(moveTo('linkTreeItems'))
          .map(moveTo(index))
          .map(moveTo(field))
          .map(setValue(value))
          .fold()
      })
    )
    .map(
      handleState(ADD_NEW_LINKTREE_ITEM, (state, action) => {
        const { name, url, icon, isPublicHistory = false } = action.payload

        return State.of(state)
          .map(moveTo('linkTreeItems'))
          .map(
            appendValue({
              number: uniqId(),
              name,
              url,
              icon,
              configuration: { isPublicHistory },
              isDefault: false,
              enabled: true
            })
          )
          .fold()
      })
    )
    .map(
      handleState(DUPLICATE_LINKTREE_ITEM, (state, action) => {
        const index = action.payload

        return State.of(state)
          .map(moveTo('linkTreeItems'))
          .map(duplicateValueWithIndex(index))
          .map(moveTo(index + 1))
          .map(mergeValue({ number: uniqId(), id: undefined }))
          .fold()
      })
    )
    .map(
      handleState(REMOVE_LINKTREE_ITEM, (state, action) => {
        return State.of(state) //
          .map(moveTo('linkTreeItems'))
          .map(removeValueWithIndex(action.payload))
          .fold()
      })
    )
    .map(
      handleState(MOVE_UP_LINKTREE_ITEM, (state, action) => {
        return State.of(state)
          .map(moveTo('linkTreeItems'))
          .map(replaceValuesWithIndexes(action.payload, action.payload - 1))
          .fold()
      })
    )
    .map(
      handleState(MOVE_DOWN_LINKTREE_ITEM, (state, action) => {
        return State.of(state)
          .map(moveTo('linkTreeItems'))
          .map(replaceValuesWithIndexes(action.payload, action.payload + 1))
          .fold()
      })
    )
    .foldState()

const Form = ({ children }) => {
  const linkTreeItems = useLinkTreeItems()
  const [state, dispatch] = useReducer(reducer, { linkTreeItems: linkTreeItems || [] })
  useOnUnload({ condition: !!state.touched })

  const handleChange = useCallback(
    curry((index, field, value) => dispatch({ type: CHANGE_LINKTREE_ITEM, payload: { index, field, value } })),
    [dispatch]
  )

  const addNewLinkTreeItem = useCallback(
    (name, url, icon) => dispatch({ type: ADD_NEW_LINKTREE_ITEM, payload: { name, url, icon } }),
    [dispatch]
  )
  const duplicateLinkTreeItem = useCallback(
    index => dispatch({ type: DUPLICATE_LINKTREE_ITEM, payload: index }),
    [dispatch]
  )
  const removeLinkTreeItem = useCallback(index => dispatch({ type: REMOVE_LINKTREE_ITEM, payload: index }), [dispatch])

  const onMoveUp = useCallback(index => dispatch({ type: MOVE_UP_LINKTREE_ITEM, payload: index }), [dispatch])
  const onMoveDown = useCallback(index => dispatch({ type: MOVE_DOWN_LINKTREE_ITEM, payload: index }), [dispatch])

  return (
    <Provider
      state={state}
      actions={{
        handleChange,
        addNewLinkTreeItem,
        duplicateLinkTreeItem,
        removeLinkTreeItem,
        onMoveUp,
        onMoveDown
      }}
    >
      {children}
    </Provider>
  )
}

export default Form
